import { useMemo, useRef, useState } from 'react';
import { Box, Checkbox, Flex, Image, useDisclosure } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import SigliveEmpty3 from 'Asset/image/siglive-empty-state-3.svg';
import { Condition, Iteration } from 'Component/others';
import EmptyState from 'Component/empty-state';
import ButtonOutline from 'UiKit/button/buttonOutline';
import ButtonSolid from 'UiKit/button/buttonSolid';
import { TextRegular, TextSemiBold } from 'UiKit/typography';
import useGetGiveawayBrief from 'Domain/productivity-host/useGetGiveawayBrief';
import DrawerUpdateGiveaway from 'View/host-stream/components/brief-live-widget/section/drawer-update-giveaway';
import { useHostStreamState } from 'View/host-stream/state';

import styles from './styles.module.css';

const GiveawayCard = ({ data }) => {
  const { t } = useTranslation('host-stream');
  const toggleDrawer = useDisclosure();
  const { productName, imagePath, dateLabel } = data;

  return (
    <Box className={styles['giveaway-card__container']}>
      <Flex gap="12px" align="center">
        <Box className={styles['giveaway-card__image-wrapper']}>
          <Condition statement={imagePath}>
            <Image src={imagePath} className={styles['giveaway-card__image']} />
          </Condition>
        </Box>
        <Box>
          <TextSemiBold className={styles['giveaway-card__name']}>{productName}</TextSemiBold>
          <TextRegular className={styles['giveaway-card__date']}>{dateLabel}</TextRegular>
        </Box>
      </Flex>
      <ButtonOutline className={styles['giveaway-card__button']} onClick={toggleDrawer.onOpen}>
        {t('update_giveaway.update_button')}
      </ButtonOutline>
      <Condition statement={toggleDrawer.isOpen}>
        <DrawerUpdateGiveaway data={data} {...toggleDrawer} />
      </Condition>
    </Box>
  );
};

const StreamUpdateGiveaway = () => {
  const { t } = useTranslation('host-stream');
  const { updateState } = useHostStreamState();
  const { data } = useGetGiveawayBrief();
  const [isChecked, setIsChecked] = useState(false);
  const [showError, setShowError] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const checkboxRef = useRef(null);

  const handleNext = () => {
    if (!isChecked) {
      setShowError(true);
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 400);
      return;
    }
    updateState({ currentStep: 3 });
  };

  const giveawayItems = useMemo(() => {
    const items = [];
    data?.giveaways?.forEach((dateGroup) => {
      dateGroup?.data?.forEach((timeGroup) => {
        timeGroup?.giveaways?.forEach((giveaway) => {
          items.push({
            ...giveaway,
            dateLabel: `${dateGroup.promotionDate} | ${timeGroup.timeRange}`,
          });
        });
      });
    });
    return items;
  }, [data]);

  return (
    <Box className={styles['stream-update-giveaway__container']}>
      <Box className={styles['stream-update-giveaway__content']}>
        <TextRegular className={styles['stream-update-giveaway__description']}>{t('update_giveaway.giveaway_question')}</TextRegular>
        <Condition
          statement={giveawayItems?.length > 0}
          fallback={
            <EmptyState icon={SigliveEmpty3} title={t('update_giveaway.giveaway_empty_title')} description={t('update_giveaway.giveaway_empty_description')} />
          }
        >
          <Flex flexDirection="column" gap="12px">
            <Iteration items={giveawayItems} render={(item, index) => <GiveawayCard key={index} data={item} />} />
          </Flex>
        </Condition>
      </Box>
      <Box className={styles['stream-update-giveaway__footer']}>
        <Box
          ref={checkboxRef}
          className={`${isShaking ? styles['stream-update-giveaway__button--shake'] : ''} ${showError ? styles['stream-update-giveaway__checkbox--error'] : ''}`}
        >
          <Checkbox
            className={styles['stream-update-giveaway__checkbox']}
            isChecked={isChecked}
            isInvalid={showError}
            onChange={(e) => {
              setIsChecked(e.target.checked);
              if (e.target.checked) setShowError(false);
            }}
          >
            <TextSemiBold
              className={`${styles['stream-update-giveaway__text--checkbox-label']} ${showError ? styles['stream-update-giveaway__text--checkbox-error'] : ''}`}
            >
              {t('update_giveaway.confirm_update_giveaway')}
            </TextSemiBold>
          </Checkbox>
        </Box>
        <ButtonSolid
          className={`${styles['stream-update-giveaway__button']} ${isShaking ? styles['stream-update-giveaway__button--shake'] : ''}`}
          onClick={handleNext}
        >
          {t('common:next')}
        </ButtonSolid>
      </Box>
    </Box>
  );
};

export default StreamUpdateGiveaway;
